import * as React from 'react';
import styled, { css } from 'styled-components';
import { CategoryLink } from '@/types/CategoryLink';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';
import { generateDiscoverLinks } from '@/redux/modules/discoverItems';
import { getCategoryLevel } from '@/utils/categoryGrabber';
import { getSimilarItemsUrl } from '@/utils/urls';
import isEqual from 'react-fast-compare';
import { ItemFacets } from '@/types/ItemFacets';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import reduce from 'lodash/reduce';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import uniqBy from 'lodash/uniqBy';
import usePrevious from '@/hooks/usePrevious';

const defaultTags = [
    { key: 'navigation.art', link: '/c/art/1/', title: <FormattedMessage id="navigation.art" /> },
    { key: 'navigation.jewelry', link: '/c/jewelry/6/', title: <FormattedMessage id="navigation.jewelry" /> },
    {
        key: 'navigation.asian_antiques',
        link: '/c/asian/158/',
        title: <FormattedMessage id="navigation.asian_antiques" />,
    },
    { key: 'navigation.furniture', link: '/c/furniture/4/', title: <FormattedMessage id="navigation.furniture" /> },
    {
        key: 'navigation.collectibles',
        link: '/c/collectibles/2/',
        title: <FormattedMessage id="navigation.collectibles" />,
    },
    {
        key: 'navigation.coins',
        link: '/c/coins-currency-stamps/24/',
        title: <FormattedMessage id="navigation.coins" />,
    },
    {
        key: 'navigation.home_and_garden',
        link: '/c/home-garden/5/',
        title: <FormattedMessage id="navigation.home_and_garden" />,
    },
    { key: 'navigation.fashion', link: '/c/fashion/3/', title: <FormattedMessage id="navigation.fashion" /> },
];

type Props = {
    categoryLinks?: CategoryLink[];
    className?: string;
    facets?: ItemFacets;
    getTagItems?: Function;
    isInHeader?: boolean;
    suggestions?: any[];
    width?: number;
};

const DiscoveryTags = ({
    categoryLinks = [],
    className = '',
    facets,
    getTagItems,
    isInHeader = false,
    suggestions = [],
    width,
}: Props) => {
    let containerRef = React.useRef<HTMLDivElement>();
    let scrollerRef = React.useRef<HTMLDivElement>();

    const [containerWidth, setContainerWidth] = React.useState(1000);
    const [left, setLeft] = React.useState(0);
    const [scrollerWidth, setScrollerWidth] = React.useState(1000);
    const [selectedTag, setSelectedTag] = React.useState('');

    const handleScrollFunc = () => {
        if (scrollerRef) {
            setLeft(scrollerRef?.current?.scrollLeft);
        }
    };

    const handleScroll = debounce(handleScrollFunc, 10);

    // Implements scroll and ui logic on every render
    React.useEffect(() => {
        if (scrollerRef.current) {
            scrollerRef.current.addEventListener('scroll', handleScroll);
            handleScroll();
            updateContainerWidth();
        }
    });

    const updateContainerWidth = React.useCallback(() => {
        let newContainerWidth = containerWidth;
        let newScrollerWidth = scrollerWidth;
        if (containerRef.current) {
            newContainerWidth = containerRef.current.getBoundingClientRect()?.width;
        }

        if (scrollerRef.current) {
            newScrollerWidth = getScrollerWidth();
        }
        setContainerWidth(newContainerWidth);
        setScrollerWidth(newScrollerWidth);
    }, [containerWidth, scrollerWidth]);

    const previousWidth = usePrevious(width);
    const previousFacets = usePrevious(facets);
    const previousSuggestions = usePrevious(suggestions);

    React.useEffect(() => {
        if (previousWidth !== width || !isEqual(previousFacets, facets) || !isEqual(previousSuggestions, suggestions)) {
            updateContainerWidth();
            setSelectedTag('');
        }
    }, [facets, previousFacets, previousSuggestions, previousWidth, suggestions, updateContainerWidth, width]);

    React.useEffect(() => {
        return () => {
            if (scrollerRef.current) {
                scrollerRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    const getScrollerWidth = () => {
        return (
            reduce(
                scrollerRef.current.children,
                (total, card: HTMLAnchorElement) => {
                    return total + card.offsetWidth;
                },
                0
            ) +
            (scrollerRef.current.children.length - 1) * 8
        ); // this last bit is for the margins
    };

    const mapLinks = ({ key, link, mainItem, secondItem, title }: any) => {
        const catalogIds = [`${mainItem?.categoryId}`, `${secondItem?.categoryId}`];
        if (!key || !title) {
            return null;
        }
        return (
            <Text colorPrimaryWeak key={key} secondary>
                <TagLink
                    $useInHeaderStyle={isInHeader}
                    $useSelectedStyle={title === selectedTag}
                    onClick={(event) => {
                        if (!isInHeader) {
                            event.preventDefault();
                            getTagItems(catalogIds, title);
                            setSelectedTag(title);
                        }
                    }}
                    route
                    to={link}
                >
                    {title}
                </TagLink>
            </Text>
        );
    };

    // copied and refactored from DiscoverItems.js
    const createCategoryLandingLink = React.useCallback((mainItem: any, secondItem: any) => {
        mainItem = getCategoryLevel(mainItem);
        secondItem = getCategoryLevel(secondItem);
        const isTwoItems = secondItem.categoryUrl;

        const key = isTwoItems ? `${mainItem.categoryLabel}-${secondItem.categoryLabel}` : `${mainItem.categoryLabel}`;

        const link = isTwoItems
            ? `/c/${mainItem.categoryUrl}/${secondItem.categoryUrl}/${mainItem.categoryId}/${secondItem.categoryId}/`
            : `/c/${mainItem.categoryUrl}/${mainItem.categoryId}/`;

        const title = isTwoItems
            ? `${secondItem.categoryLabel} ${mainItem.categoryLabel}`
            : `${mainItem.categoryLabel}`;

        return { key, link, mainItem, secondItem, title };
    }, []);

    const mapSuggestions = ({ label: title, value }: any, index: number) => {
        const link = getSimilarItemsUrl(value);
        return { key: `${index}-${value}`, link, title };
    };

    const suggestionLinks = suggestions.map(mapSuggestions);
    const facetLinks = generateDiscoverLinks(facets, createCategoryLandingLink);

    let linkObjects = [...suggestionLinks, ...categoryLinks, ...facetLinks];
    if (!linkObjects.length) {
        linkObjects = defaultTags;
    }

    const tags = uniqBy(linkObjects, 'link');

    return (
        <DiscoveryTagsDiv className={className} ref={(ref) => (containerRef.current = ref)}>
            <TagsDiv ref={(ref) => (scrollerRef.current = ref)}>{tags.map(mapLinks)}</TagsDiv>
            <LeftFadeDiv useShowFadeStyles={left > 5} />
            <RightFadeDiv useShowFadeStyles={left + containerWidth + 5 < scrollerWidth} />
        </DiscoveryTagsDiv>
    );
};

export default React.memo<Props>(DiscoveryTags, isEqual);

const DiscoveryTagsDiv = styled.div`
    position: relative;
    z-index: 1;
    padding: 0;
    height: 38px;
    overflow: hidden;
    margin: 16px 0 21px;
`;

const SelectedStyle = css`
    background-color: ${({ theme }) => theme.colors.grey400};
    color: ${({ theme }) => theme.colors.black};
    text-decoration: none;
`;

const TagLink = styled(Link)<{ $useInHeaderStyle; $useSelectedStyle }>`
    &&& {
        background-color: ${({ theme }) => theme.colors.grey500};
        border-radius: 2px;
        height: 36px;
        display: inline-flex;
        padding: 0 8px;
        align-items: center;
        letter-spacing: 0.5px;
        text-decoration: none;
        box-shadow: 0 0 2px ${({ theme }) => theme.colors.shadowOverlay2};
        margin-right: 15px !important;
        color: ${({ theme }) => theme.colors.grey100};

        &:first-child {
            margin-left: 1px;
        }

        &:last-child {
            margin-right: 4px;
        }

        &:hover,
        &:focus {
            ${SelectedStyle}
        }

        ${({ $useSelectedStyle }) => $useSelectedStyle && SelectedStyle}
        ${({ $useInHeaderStyle }) =>
            $useInHeaderStyle &&
            css`
                &&& {
                    box-shadow: none;
                    background-color: transparent;
                    padding: 0;
                    margin: 0 !important;
                }
            `}
    }
`;

const TagsDiv = styled.div`
    &&& {
        margin: 0;
        width: 100%;
        display: inline-block;
        white-space: nowrap;
        overflow: -moz-scrollbars-none;
        -ms-overflow-style: none;
        overflow-x: auto;
        overflow-y: hidden;
        height: 55px;
    }

    ::-webkit-scrollbar {
        display: none !important;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 7px;
    }
`;

const LeftFadeDiv = styled.div<{ useShowFadeStyles }>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${({ theme }) => theme.colors.white} 100%);
    display: none;
    ${({ useShowFadeStyles }) =>
        useShowFadeStyles &&
        css`
            display: block;
        `}
`;

const RightFadeDiv = styled.div<{ useShowFadeStyles }>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${({ theme }) => theme.colors.white} 100%);
    display: none;
    ${({ useShowFadeStyles }) =>
        useShowFadeStyles &&
        css`
            display: block;
        `}
`;
